import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2, Paperclip, Search } from "lucide-react";
import { api, apiError, fileUrl, inr, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function InventoryBills() {
  const [data, setData] = useState({ bills: [], suppliers: [], years: [] });
  const [drive, setDrive] = useState(null);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zipping, setZipping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (year !== "all") params.set("year", year);
      const { data: d } = await api.get(`/inventory/bills?${params}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, year]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/inventory/drive-status").then(({ data: d }) => setDrive(d)).catch(() => {});
  }, []);

  const rows = data.bills.filter((b) => supplier === "all" || b.supplier_name === supplier);
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await api.get(`/inventory/bills/download-zip?ids=${selected.join(",")}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pramsia-inventory-bills-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.length} bill(s) downloaded as one ZIP`);
    } catch (e) {
      const detail = e?.response?.data instanceof Blob
        ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } })
        : apiError(e);
      toast.error(detail || "Could not prepare the ZIP");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="inventory-bills">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="bills-search-input" className="pl-9"
            placeholder="Invoice no, supplier or item" value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger data-testid="bills-year-filter" className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {(data.years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supplier} onValueChange={setSupplier}>
          <SelectTrigger data-testid="bills-supplier-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {(data.suppliers || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {data.can_download_all && (
          <Button size="sm" className="gap-1.5" data-testid="bills-download-zip-button"
            disabled={selected.length === 0 || zipping} onClick={downloadZip}>
            {zipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Download {selected.length || ""} as ZIP
          </Button>
        )}
      </div>

      {drive && !drive.configured && (
        <p className="text-[11px] text-muted-foreground" data-testid="drive-status-note">
          Bills are stored in the ERP bill vault ({drive.structure}). Google Drive copies start
          automatically once the Drive folder ID and Google credentials are added.
        </p>
      )}

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="surface overflow-x-auto" data-testid="bills-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                {data.can_download_all && (
                  <th className="w-8 py-2 pl-3">
                    <Checkbox data-testid="bills-select-all" checked={allSelected}
                      onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])} />
                  </th>
                )}
                <th className="py-2 pr-2">Invoice</th>
                <th className="py-2 pr-2">Supplier</th>
                <th className="py-2 pr-2">Item</th>
                <th className="py-2 pr-2 text-right">Qty</th>
                {data.can_see_amount && <th className="py-2 pr-2 text-right">Amount</th>}
                <th className="hidden py-2 pr-2 md:table-cell">Folder</th>
                <th className="py-2 pr-3 text-right">Bill</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0"
                  data-testid={`bill-row-${b.id}`}>
                  {data.can_download_all && (
                    <td className="py-2 pl-3">
                      <Checkbox data-testid={`bill-select-${b.id}`} checked={selected.includes(b.id)}
                        onCheckedChange={() => toggle(b.id)} />
                    </td>
                  )}
                  <td className="py-2 pr-2">
                    <div className="font-medium">{b.invoice_no || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{shortDate(b.invoice_date)}</div>
                  </td>
                  <td className="py-2 pr-2">{b.supplier_name || "—"}</td>
                  <td className="py-2 pr-2">
                    {b.item_name}
                    <div className="text-[10px] text-muted-foreground">{b.warehouse_name}</div>
                  </td>
                  <td className="py-2 pr-2 text-right font-mono-num">{b.quantity} {b.unit}</td>
                  {data.can_see_amount && (
                    <td className="py-2 pr-2 text-right font-mono-num">{inr(b.amount)}</td>
                  )}
                  <td className="hidden py-2 pr-2 text-[10px] text-muted-foreground md:table-cell">
                    {b.drive_path}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {b.files.length === 0 ? (
                      <Badge variant="outline" className="text-[9px]">no file</Badge>
                    ) : (
                      <div className="flex flex-wrap justify-end gap-1">
                        {b.files.map((f) => (
                          <span key={f.file_id} className="flex items-center gap-1">
                            <a href={fileUrl(f.url)} target="_blank" rel="noreferrer"
                              title={`View ${f.filename}`} data-testid={`bill-view-${f.file_id}`}
                              className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 hover:bg-secondary">
                              <Paperclip className="h-3 w-3" /> View
                            </a>
                            <a href={fileUrl(f.url)} download={f.filename}
                              title={`Download ${f.filename}`} data-testid={`bill-download-${f.file_id}`}
                              className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 hover:bg-secondary">
                              <Download className="h-3 w-3" />
                            </a>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-muted-foreground">
                    <FileText className="mx-auto mb-2 h-6 w-6 opacity-40" />
                    No purchase bills recorded yet. Use Stock In to add one with its invoice.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
